import React from "react";

import { defineMessages } from "react-intl";

import { InstantBook } from "../../shared-components/icons";
import { isFsvListing } from "../utils/listing-fsv.utils";

const translationMessages = defineMessages({
  productionTitle: {
    id: "hosting.toc_by_use_type.media_productions",
    defaultMessage: "media productions",
  },
  productionModalTitle: {
    id: "hosting.toc_by_use_type.modal_title_production",
    defaultMessage: "production",
  },
  productionDescription: {
    id: "hosting.toc_by_use_type.production_description",
    defaultMessage: "Video shoots, photo shoots, audio recording, etc.",
  },
  productionOptionLabel: {
    id: "hosting.toc_by_use_type.option_label_production",
    defaultMessage: "Production",
  },
  offSiteTitle: {
    id: "hosting.toc_by_use_type.meeting",
    defaultMessage: "meetings",
  },
  offSiteModalTitle: {
    id: "hosting.toc_by_use_type.modal_title_meeting",
    defaultMessage: "meeting",
  },
  offSiteDescription: {
    id: "hosting.toc_by_use_type.meeting_description",
    defaultMessage: "Offsites, workshops, trainings, retreats, etc.",
  },
  offSiteOptionLabel: {
    id: "hosting.toc_by_use_type.option_label_off-site",
    defaultMessage: "Meeting",
  },
  eventTitle: {
    id: "hosting.toc_by_use_type.events",
    defaultMessage: "events",
  },
  eventModalTitle: {
    id: "hosting.toc_by_use_type.modal_title_events",
    defaultMessage: "event",
  },
  eventDescription: {
    id: "hosting.toc_by_use_type.events_description",
    defaultMessage: "Birthday parties, baby showers, holiday parties, etc.",
  },
  eventOptionLabel: {
    id: "hosting.toc_by_use_type.option_label_event",
    defaultMessage: "Event",
  },
  photosHeader: {
    id: "uploader.page.header",
    defaultMessage: "Photos and video",
  },
  photosDescription: {
    id: "hosting.listing_toc.photos_description",
    defaultMessage: "Select cover photo, add, sort and delete",
  },
  listingDetailsHeader: {
    id: "hosting.listing_toc.listing_details_header",
    defaultMessage: "Listing details",
  },
  listingDetailsDescription: {
    id: "hosting.listing_toc.listing_details_description",
    defaultMessage: "Title, description, amenities",
  },
  rulesHeader: {
    id: "hosting.listing_toc.rules_header",
    defaultMessage: "Rules",
  },
  rulesDescription: {
    id: "hosting.listing_toc.rules_description",
    defaultMessage: "General rules, minimum hours, capacity, minimum age",
  },
  pricingHeader: {
    id: "hosting.listing_toc.pricing_details_header",
    defaultMessage: "Pricing details",
  },
  pricingDescription: {
    id: "hosting.listing_toc.pricing_description",
    defaultMessage:
      "Base rate, calendar pricing, attendee pricing, discounts and cleaning fees",
  },
  fsvPricingDescription: {
    id: "hosting.listing_toc.fsv_pricing_description",
    defaultMessage:
      "Food and drink minimum, space rental rate, gratuity, room type, menu",
  },
  instantBookHeader: {
    id: "hosting.listing_toc.instant_book_header",
    defaultMessage: "Instant Book",
  },
  instantBookDescription: {
    id: "hosting.listing_toc.instant_book_description",
    defaultMessage:
      "Get more bookings by letting guests instantly book your space",
  },
  productionFaqHeader: {
    id: "hosting.listing_toc.production_faq_header",
    defaultMessage: "Production FAQ",
  },
  productionFaqDescription: {
    id: "hosting.listing_toc.production_faq_description",
    defaultMessage:
      "Answer frequently asked questions and rules about production bookings",
  },
  eventFaqHeader: {
    id: "hosting.listing_toc.event_faq_header_recommended",
    defaultMessage: "Event FAQ - Recommended",
  },
  eventFaqDescription: {
    id: "hosting.listing_toc.event_faq_description",
    defaultMessage:
      "Answer frequently asked questions and rules about event bookings",
  },
  newPhotosDescription: {
    id: "uploader.video_modal.title",
    defaultMessage: "Add videos",
  },
  newFAQDescription: {
    id: "hosting.active_listings_panel.set_up_faq_text",
    defaultMessage: "Set up your FAQ",
  },
});

// Full Service Event (FSV) copy shared by the active and deactivated cards
export const fsvMessages = defineMessages({
  title: {
    id: "hosting.active_listings_panel.full_service_event_title",
    defaultMessage: "Full Service Events",
  },
  description: {
    id: "hosting.active_listings_panel.full_service_event_description",
    defaultMessage:
      "Birthday parties, holiday parties, and events with a required food and drink minimum.",
  },
});

export const tocByUseType = {
  Production: {
    title: translationMessages.productionTitle,
    modalTitle: translationMessages.productionModalTitle,
    toc: [
      "photos",
      "listing-details",
      "rules",
      "pricing",
      "instant-book",
      "production-faq",
    ],
    icon: "productions",
    description: translationMessages.productionDescription,
    optionLabel: translationMessages.productionOptionLabel,
  },
  "Off-Site": {
    title: translationMessages.offSiteTitle,
    modalTitle: translationMessages.offSiteModalTitle,
    toc: ["photos", "listing-details", "rules", "pricing", "instant-book"],
    icon: "meetings",
    description: translationMessages.offSiteDescription,
    optionLabel: translationMessages.offSiteOptionLabel,
  },
  Event: {
    title: translationMessages.eventTitle,
    modalTitle: translationMessages.eventModalTitle,
    toc: [
      "photos",
      "listing-details",
      "rules",
      "pricing",
      "instant-book",
      "event-faq",
    ],
    icon: "events",
    description: translationMessages.eventDescription,
    optionLabel: translationMessages.eventOptionLabel,
  },
};

export const listingTOC = (listing = {}) => {
  // FSV listings get their own pricing page and description
  const isFsv = isFsvListing(listing);

  return [
    {
      key: "photos",
      header: translationMessages.photosHeader,
      description: translationMessages.photosDescription,
      link: `/mylistings/${listing._id}/photos`,
      newDescription: translationMessages.newPhotosDescription,
    },
    {
      key: "listing-details",
      header: translationMessages.listingDetailsHeader,
      description: translationMessages.listingDetailsDescription,
      link: `/mylistings/${listing._id}/listing-details`,
    },
    {
      key: "rules",
      header: translationMessages.rulesHeader,
      description: translationMessages.rulesDescription,
      link: `/mylistings/${listing._id}/rules`,
    },
    {
      key: "pricing",
      header: translationMessages.pricingHeader,
      description: isFsv
        ? translationMessages.fsvPricingDescription
        : translationMessages.pricingDescription,
      link: isFsv
        ? `/mylistings/${listing._id}/fsv`
        : `/mylistings/${listing._id}/price`,
    },
    {
      key: "instant-book",
      header: translationMessages.instantBookHeader,
      header_icon: (
        <InstantBook className="margin-md-left" style={{ height: 20 }} />
      ),
      description: translationMessages.instantBookDescription,
      link: `/mylistings/${listing._id}/instant-book`,
    },
    {
      key: "production-faq",
      header: translationMessages.productionFaqHeader,
      description: translationMessages.productionFaqDescription,
      link: `/your-space/${listing.parentSpaceId}/production-faqs/lighting`, // Space id
    },
    {
      key: "event-faq",
      header: translationMessages.eventFaqHeader,
      description: translationMessages.eventFaqDescription,
      link: `/your-space/${listing.parentSpaceId}/event-faqs/food_beverage`, // Space id
    },
  ];
};
